import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "./Logo";
import { nav, primaryCta, contact } from "@/content/site";
import { ease } from "./Motion";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    document.documentElement.style.overflow = open ? "hidden" : "";
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <a href="#contenu" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[90] focus:bg-ink focus:px-4 focus:py-2 focus:text-ivory">Aller au contenu</a>
      <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-300 ${scrolled ? "border-b border-ink/15 bg-ivory/85 backdrop-blur-xl" : "border-b border-transparent"}`}>
        <div className="wrap flex h-[72px] items-center justify-between gap-6">
          <Link to="/" aria-label="SANAA DESIGN STUDIO — accueil" data-testid="header-logo"><Logo /></Link>
          <nav aria-label="Navigation principale" className="hidden xl:block">
            <ul className="flex items-center gap-6">
              {nav.map((n) => (
                <li key={n.to}>
                  <NavLink to={n.to} data-testid={`nav-${n.to.slice(1)}`} className="group relative text-[14px] font-medium text-ink/80 hover:text-ink aria-[current=page]:text-ink">
                    {n.label}
                    <span className="absolute -bottom-2 left-1/2 h-1 w-1 -translate-x-1/2 bg-accent opacity-0 transition-opacity group-aria-[current=page]:opacity-100" />
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
          <div className="flex items-center gap-3">
            <Link to={primaryCta.to} data-track="cta_diagnostic" data-testid="header-cta" className="hidden bg-ink px-5 py-3 text-[14px] font-semibold text-ivory transition-colors duration-300 hover:bg-accent sm:inline-flex">
              Diagnostic digital
            </Link>
            <button type="button" onClick={() => setOpen(true)} aria-label="Ouvrir le menu" aria-expanded={open} data-testid="menu-open" className="grid h-12 w-12 place-items-center border border-ink/25 xl:hidden">
              <span className="flex flex-col gap-[5px]"><span className="block h-px w-5 bg-ink" /><span className="block h-px w-5 bg-ink" /></span>
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div role="dialog" aria-modal="true" aria-label="Menu" data-testid="mobile-menu" className="on-dark fixed inset-0 z-[70] flex flex-col bg-ink text-ivory"
            initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} exit={{ clipPath: "inset(0 0 100% 0)" }} transition={{ duration: 0.6, ease }}>
            <div className="wrap flex h-[72px] items-center justify-between">
              <Logo tone="light" />
              <button type="button" onClick={() => setOpen(false)} aria-label="Fermer le menu" data-testid="menu-close" className="grid h-12 w-12 place-items-center border border-ivory/30 text-xl">×</button>
            </div>
            <nav aria-label="Navigation mobile" className="wrap flex-1 overflow-y-auto pt-4">
              <ul>
                {[{ label: "Accueil", to: "/" }, ...nav].map((n, i) => (
                  <motion.li key={n.to} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 + i * 0.04, duration: 0.5, ease }} className="border-b border-ivory/10">
                    <NavLink to={n.to} end data-testid={`mobile-nav-${n.to.slice(1) || "home"}`} className="flex items-baseline justify-between py-3 aria-[current=page]:text-[#9AA3FF]">
                      <span className="display text-[2.1rem] leading-tight">{n.label}</span>
                      <span className="font-mono text-[12px] text-[#B3BAB4]">0{i + 1}</span>
                    </NavLink>
                  </motion.li>
                ))}
              </ul>
            </nav>
            <div className="wrap flex flex-col gap-3 pb-8 pt-6">
              <Link to={primaryCta.to} data-testid="mobile-menu-cta" className="bg-ivory py-4 text-center text-[15px] font-semibold text-ink">{primaryCta.label}</Link>
              <a href={`tel:${contact.phoneIntl}`} data-track="phone_click" data-testid="mobile-menu-call" className="border border-ivory/40 py-4 text-center text-[15px] font-semibold">Appeler — {contact.phone}</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
